import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, X, Minus } from "lucide-react";
import {
  apiRequest,
  downloadWithAuth,
  profileLabel,
  WIZARD_STEPS as steps,
} from "../utils/api";

// ---------------------------------------------------------------------
// Outcome
//   VERIFIED  -> verification ran and every required check passed
//   FAILED    -> verification ran and at least one required check failed
//   PENDING   -> the artifact is signed but verification has not completed
// "Success" is only ever shown for VERIFIED.
// ---------------------------------------------------------------------
const outcomeOf = (verification) => {
  const status = verification?.verification_status;
  if (status === "VERIFIED") return "VERIFIED";
  if (status === "FAILED") return "FAILED";
  return "PENDING";
};

const OUTCOME_STYLE = {
  VERIFIED: {
    bg: "#F0FDF4", border: "#BBF7D0", color: "#15803D",
    title: "Artifact signed and verified",
    text: "The signature was checked against the stored artifact and manifest and passed.",
  },
  FAILED: {
    bg: "#FEF2F2", border: "#FECACA", color: "#B91C1C",
    title: "Signed, but verification FAILED",
    text: "Do not deploy this artifact. At least one required check failed — see the details below.",
  },
  PENDING: {
    bg: "#FFFBEB", border: "#FDE68A", color: "#B45309",
    title: "Signed — verification has not completed",
    text: "The signature was created, but it has not been verified yet. Use “Verify again”.",
  },
};

// true / false / null per outcome (null = not decided yet)
const OUTCOME_OK = { VERIFIED: true, FAILED: false, PENDING: null };

const COMPLETION_DETAIL = {
  VERIFIED: "Ready to use",
  FAILED: "Not safe to use",
  PENDING: "Waiting for verification",
};

const okKey = (ok) => {
  if (ok === true) return "pass";
  if (ok === false) return "fail";
  return "none";
};

const STAGE_BADGE = {
  pass: { bg: "bg-green-500", icon: <Check size={12} /> },
  fail: { bg: "bg-red-500", icon: <X size={12} /> },
  none: { bg: "bg-amber-400", icon: <Minus size={12} /> },
};

const STEP_STYLE = {
  failed: { circle: "bg-red-500", text: "text-red-600" },
  done: { circle: "bg-green-500", text: "text-green-600" },
  active: { circle: "bg-blue-600", text: "text-blue-600" },
};

const triState = (value) => {
  if (value === true) return { label: "Passed", color: "#15803D", icon: <Check size={13} /> };
  if (value === false) return { label: "Failed", color: "#B91C1C", icon: <X size={13} /> };
  return { label: "Not applicable", color: "#9CA3AF", icon: <Minus size={13} /> };
};

const downloadNames = (identityType) => {
  const t = String(identityType || "").toLowerCase();
  if (t === "hybrid") return { signature: "signature.json", publicKey: "public_key.json" };
  if (t === "pq") return { signature: "signature.sig", publicKey: "public_key.bin" };
  return { signature: "signature.sig", publicKey: "public_key.pem" };
};

// ---------------------------------------------------------------------
// Row builders (pure functions)
// ---------------------------------------------------------------------
const verificationDetail = ({ outcome, verifying, verificationError }) => {
  if (outcome === "VERIFIED") return "All required checks passed";
  if (outcome === "FAILED") return "A required check failed";
  if (verifying) return "Verifying…";
  return verificationError || "Not completed";
};

const buildStageRows = (ctx, { outcome, verifying, verificationError }) => {
  const { artifactId, fileName, manifest, manifestSha256, signResult } = ctx;
  const ok = OUTCOME_OK[outcome];
  return [
    { label: "Upload", ok: true, detail: `Artifact #${artifactId} stored (${fileName})` },
    { label: "Manifest", ok: true, detail: `${manifest?.manifest_id || ""} — SHA-256 ${(manifestSha256 || "").slice(0, 16)}…` },
    { label: "Signing", ok: true, detail: `Signature #${signResult.signature_id}${signResult.already_signed ? " (existing signature returned)" : ""}` },
    { label: "Verification", ok, detail: verificationDetail({ outcome, verifying, verificationError }) },
    { label: "Completion", ok, detail: COMPLETION_DETAIL[outcome] },
  ];
};

const buildChecks = (v, outcome) => {
  if (!v || outcome === "PENDING") return [];
  const hybrid = v.pqc_signature_valid != null && v.classical_signature_valid != null;
  return [
    ["Artifact unchanged since signing", v.hash_match],
    ["Signature valid", v.signature_valid],
    ...(hybrid
      ? [
          ["   Post-quantum part", v.pqc_signature_valid],
          ["   ECDSA part", v.classical_signature_valid],
        ]
      : []),
    ["Manifest bound to the signature", v.manifest_valid],
    ["Internal timestamp", v.timestamp_valid],
    ["Internal ledger entry", v.blockchain_valid],
    ["Certificate record", v.certificate_valid],
  ];
};

const optionalRow = (label, value, mono) => (value ? [label, value, mono] : null);

const buildSummaryRows = (ctx, verification) => {
  const { fileName, manifestSha256, selectedDigitalId, signResult } = ctx;
  return [
    ["Artifact", fileName],
    ["Artifact SHA-256", signResult.signed_hash, true],
    ["Manifest SHA-256", signResult.manifest_sha256 || manifestSha256, true],
    ["Digital ID", selectedDigitalId ? `${selectedDigitalId.identity_name} (#${selectedDigitalId.id})` : `#${signResult.digital_id}`],
    ["Algorithm", signResult.algorithm],
    ["Signing profile", profileLabel(selectedDigitalId?.identity_type)],
    ["Signed at", signResult.signed_at ? new Date(signResult.signed_at).toLocaleString() : ""],
    optionalRow("Certificate serial", signResult.certificate_serial, true),
    optionalRow("Internal timestamp", signResult.timestamp),
    optionalRow("Ledger block hash", signResult.block_hash, true),
    optionalRow("IPFS CID", signResult.ipfs_cid, true),
    optionalRow("Trust level", verification?.trust_level),
  ].filter(Boolean);
};

// ---------------------------------------------------------------------
// Presentational components
// ---------------------------------------------------------------------
const stepState = (failed, done) => {
  if (failed) return "failed";
  return done ? "done" : "active";
};

const StepIcon = ({ state, index }) => {
  if (state === "failed") return <X size={14} />;
  if (state === "done") return <Check size={14} />;
  return <>{index + 1}</>;
};

const Stepper = ({ outcome }) => (
  <div className="flex items-center justify-between relative mb-5" style={{ height: "72px" }}>
    {steps.map((step, index) => {
      const last = index === steps.length - 1;
      const state = stepState(last && outcome === "FAILED", index < 5 || outcome === "VERIFIED");
      const s = STEP_STYLE[state];
      return (
        <div key={step} className="flex-1 flex flex-col items-center relative">
          {!last && <div className="absolute top-5 left-1/2 w-full h-[2px] bg-green-500" />}
          <div
            className={`z-10 rounded-full flex items-center justify-center text-xs font-semibold text-white ${s.circle}`}
            style={{ width: "40px", height: "40px" }}
          >
            <StepIcon state={state} index={index} />
          </div>
          <p className={`mt-2 text-[14px] font-medium text-center ${s.text}`}>{step}</p>
        </div>
      );
    })}
  </div>
);

const NoResult = ({ onStart }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <p className="text-gray-500 text-sm mb-4">
      No signing result found. Sign an artifact first, then this page shows the outcome.
    </p>
    <button
      onClick={onStart}
      className="text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-[10px]"
      style={{ height: "44px", padding: "0 24px" }}
    >
      Start a new signing
    </button>
  </div>
);

const OutcomeBanner = ({ outcome, verificationError }) => {
  const style = OUTCOME_STYLE[outcome];
  return (
    <div
      style={{
        background: style.bg, border: `1px solid ${style.border}`, color: style.color,
        borderRadius: 12, padding: "16px 20px", marginBottom: 16,
      }}
    >
      <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{style.title}</p>
      <p style={{ fontSize: 13.5, margin: "4px 0 0 0" }}>{style.text}</p>
      {verificationError && outcome === "PENDING" && (
        <p style={{ fontSize: 13, margin: "6px 0 0 0" }}>{verificationError}</p>
      )}
    </div>
  );
};

const StageList = ({ rows }) => (
  <div className="border rounded-2xl bg-white mb-4" style={{ borderColor: "#E2E8F0" }}>
    {rows.map((row, i) => {
      const badge = STAGE_BADGE[okKey(row.ok)];
      return (
        <div
          key={row.label}
          className="flex items-center gap-3 px-5 py-3"
          style={{ borderBottom: i < rows.length - 1 ? "1px solid #F1F5F9" : "none" }}
        >
          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white ${badge.bg}`}>
            {badge.icon}
          </div>
          <span className="text-sm font-medium text-gray-800" style={{ width: 110 }}>{row.label}</span>
          <span className="text-xs text-gray-500 break-all">{row.detail}</span>
        </div>
      );
    })}
  </div>
);

const ChecksCard = ({ checks, findings, certificateWarning }) => {
  if (checks.length === 0) return null;
  return (
    <div className="border rounded-2xl bg-white mb-4 p-4" style={{ borderColor: "#E2E8F0" }}>
      <p className="text-sm font-semibold text-gray-800 mb-2">Verification checks</p>
      {checks.map(([label, value]) => {
        const t = triState(value);
        return (
          <div key={label} className="flex items-center justify-between py-1.5 text-sm">
            <span className="text-gray-600" style={{ whiteSpace: "pre" }}>{label}</span>
            <span className="flex items-center gap-1 font-medium" style={{ color: t.color }}>
              {t.icon} {t.label}
            </span>
          </div>
        );
      })}
      {findings?.length > 0 && (
        <p className="text-xs text-amber-700 mt-2">
          {findings.length} structural finding(s) were reported against the uploaded baseline.
        </p>
      )}
      {certificateWarning && <p className="text-xs text-amber-700 mt-2">{certificateWarning}</p>}
    </div>
  );
};

const SummaryCard = ({ rows }) => (
  <div className="border rounded-2xl bg-white mb-4" style={{ borderColor: "#E2E8F0" }}>
    <div className="px-5 pt-4 pb-2 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
      Signing summary
    </div>
    {rows.map(([label, value, mono], i) => (
      <div
        key={label}
        className="flex justify-between items-start gap-4 px-5 py-2.5"
        style={{ borderBottom: i < rows.length - 1 ? "1px solid #F1F5F9" : "none" }}
      >
        <span className="text-[12.5px] text-gray-500 font-medium" style={{ whiteSpace: "nowrap" }}>{label}</span>
        <span
          className="text-right break-all font-medium text-gray-800"
          style={{ fontSize: mono ? 11.5 : 12.5, fontFamily: mono ? "'JetBrains Mono', monospace" : "inherit" }}
        >
          {value}
        </span>
      </div>
    ))}
  </div>
);

const ActionButtons = ({ outputs, outcome, downloading, verifying, onDownload, onVerify, onIntegrations, onNew }) => (
  <>
    <div className="flex gap-2.5 mb-3">
      <button
        onClick={() => onDownload([outputs[0]])}
        disabled={downloading || outcome === "FAILED"}
        className="flex-1 text-sm font-semibold text-white rounded-[10px] disabled:opacity-50"
        style={{ padding: "11px 16px", background: "#2563EB" }}
      >
        {downloading ? "Downloading…" : "Download signed artifact"}
      </button>
      <button
        onClick={() => onDownload(outputs)}
        disabled={downloading}
        className="flex-1 text-sm font-semibold rounded-[10px] border disabled:opacity-50"
        style={{ padding: "11px 16px", background: "#F1F5F9", color: "#334155", borderColor: "#E2E8F0" }}
      >
        Download all 4 files
      </button>
    </div>
    <div className="flex gap-2.5">
      <button
        onClick={onVerify}
        disabled={verifying}
        className="flex-1 text-sm font-medium rounded-[10px] border disabled:opacity-50"
        style={{ padding: "10px 16px", background: "white", color: "#334155", borderColor: "#E2E8F0" }}
      >
        {verifying ? "Verifying…" : "Verify again"}
      </button>
      <button
        onClick={onIntegrations}
        className="flex-1 text-sm font-medium rounded-[10px] border"
        style={{ padding: "10px 16px", background: "white", color: "#334155", borderColor: "#E2E8F0" }}
      >
        Integrations &amp; more outputs
      </button>
    </div>

    <div className="flex justify-end mt-4">
      <button
        onClick={onNew}
        style={{ background: "none", border: "none", color: "#2563eb", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
      >
        Sign another artifact →
      </button>
    </div>
  </>
);

const ResultView = ({
  ctx, verification, verificationError, verifying, outcome, outputs,
  downloading, downloadError, onDownload, onVerify, onIntegrations, onNew,
}) => (
  <div className="max-w-2xl mx-auto">
    <OutcomeBanner outcome={outcome} verificationError={verificationError} />
    <StageList rows={buildStageRows(ctx, { outcome, verifying, verificationError })} />
    <ChecksCard
      checks={buildChecks(verification, outcome)}
      findings={verification?.findings}
      certificateWarning={ctx.signResult.certificate_warning}
    />
    <SummaryCard rows={buildSummaryRows(ctx, verification)} />

    {downloadError && <p className="text-sm text-red-600 mb-3">{downloadError}</p>}

    <ActionButtons
      outputs={outputs}
      outcome={outcome}
      downloading={downloading}
      verifying={verifying}
      onDownload={onDownload}
      onVerify={onVerify}
      onIntegrations={onIntegrations}
      onNew={onNew}
    />
  </div>
);

// ---------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------
export default function Complete() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const {
    artifactId,
    manifest,
    manifestSha256,
    selectedDigitalId,
    signResult,
    verificationError: initialVerificationError,
  } = state;

  const [verification, setVerification] = useState(state.verification || null);
  const [verifying, setVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState(initialVerificationError || "");
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const hasResult = artifactId != null && !!signResult;
  const outcome = outcomeOf(verification);
  const fileName = manifest?.firmware?.file_name || "";

  const runVerification = async () => {
    setVerifying(true);
    setVerificationError("");
    try {
      const data = await apiRequest("/api/artifact/verify", {
        method: "POST",
        json: { artifact_id: artifactId, signature_id: signResult?.signature_id },
      });
      setVerification(data);
    } catch (err) {
      setVerificationError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  // Landing here without a verification result (for example after a failed
  // verify call) runs it once automatically.
  useEffect(() => {
    if (hasResult && !verification && !initialVerificationError) {
      runVerification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const names = downloadNames(selectedDigitalId?.identity_type);

  const outputs = [
    { path: `/api/artifact/download/${artifactId}`, fallback: `signed_${fileName || "artifact"}`, label: "Signed artifact" },
    { path: `/api/artifact/download/signature/${artifactId}`, fallback: names.signature, label: "Signature" },
    { path: `/api/artifact/download/manifest/${artifactId}`, fallback: "manifest.json", label: "Manifest" },
    { path: `/api/artifact/download/public-key/${artifactId}`, fallback: names.publicKey, label: "Public key" },
  ];

  const handleDownload = async (items) => {
    setDownloadError("");
    setDownloading(true);
    try {
      for (const item of items) {
        await downloadWithAuth(item.path, item.fallback);
      }
    } catch (err) {
      setDownloadError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const goUpload = () => navigate("/upload-artifact");

  return (
    <div style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <Stepper outcome={outcome} />

      {hasResult ? (
        <ResultView
          ctx={{ artifactId, fileName, manifest, manifestSha256, selectedDigitalId, signResult }}
          verification={verification}
          verificationError={verificationError}
          verifying={verifying}
          outcome={outcome}
          outputs={outputs}
          downloading={downloading}
          downloadError={downloadError}
          onDownload={handleDownload}
          onVerify={runVerification}
          onIntegrations={() => navigate("/integration", { state })}
          onNew={goUpload}
        />
      ) : (
        <NoResult onStart={goUpload} />
      )}
    </div>
  );
}